import type { CatalogProcedureOption } from '@/features/catalog/api'

/**
 * O arquivo da importação de procedimentos do lado do app (docs/api/admin.md,
 * "POST /admin/procedures/import"; RN-ADM-37): o modelo que a pessoa baixa e a leitura do arquivo
 * que ela escolhe. Quem valida é a API; aqui só se monta e se lê texto.
 */

/**
 * As colunas do modelo, na ordem em que ele as traz. São as que a API reconhece; a coluna `tipo`
 * saiu em 15/10/2026 (T47): o tipo sai da composição, e a API ignora a coluna em arquivo antigo.
 */
export const IMPORT_COLUMNS = [
  'nome',
  'procedimento',
  'valor',
  'vigencia',
  'meios',
  'parcelas',
  'juros',
  'desconto',
  'descontoMeios',
  'descricao',
] as const

/** O limite da API: 1 MiB. Arquivo maior nem é lido. */
export const IMPORT_MAX_BYTES = 1_048_576

export const IMPORT_TEMPLATE_FILE_NAME = 'modelo-procedimentos.csv'

/** Texto com ponto e vírgula, aspas ou quebra de linha vai entre aspas, com a aspa dobrada. */
function escapeCell(text: string): string {
  return /[;"\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

/**
 * O modelo do CSV: o cabeçalho e uma linha para cada procedimento da lista da Westhetics que a
 * clínica ainda não tem, com o nome certo já na coluna `procedimento`. A pessoa preenche valor e
 * meios do que oferece e apaga as linhas do que não oferece.
 */
export function buildImportTemplate(
  options: readonly Pick<CatalogProcedureOption, 'name' | 'isInClinicCatalog'>[],
): string {
  const rows = options
    .filter((option) => !option.isInClinicCatalog)
    .map((option) =>
      IMPORT_COLUMNS.map((column) =>
        column === 'procedimento' ? escapeCell(option.name) : '',
      ).join(';'),
    )
  return `${[IMPORT_COLUMNS.join(';'), ...rows].join('\r\n')}\r\n`
}

/** O endereço de download do modelo. O BOM no começo faz o Excel abrir os acentos certos. */
export function importTemplateHref(csv: string): string {
  return `data:text/csv;charset=utf-8,${encodeURIComponent(`\uFEFF${csv}`)}`
}

/**
 * O texto do arquivo escolhido. O contrato é UTF-8; o "CSV (separado por vírgulas)" do Excel em
 * português sai em Windows-1252, e é lido assim quando o arquivo não é UTF-8 válido, para os
 * acentos chegarem inteiros à API.
 */
export async function readCsvFile(file: Blob): Promise<string> {
  const bytes = await file.arrayBuffer()
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    return new TextDecoder('windows-1252').decode(bytes)
  }
}
